import ApiService from "./api.service";
import { collection, getDocs } from "firebase/firestore";
import { auth, db, getCurrentIdToken } from "../firebase/firebaseClient";

export type PaymentCardType = "VISA" | "MASTERCARD";

export interface CreatePaymentInstrumentRequest {
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  cardHolderName: string;
  securityCode: string;
  cardType: PaymentCardType;
}

export interface PaymentInstrumentSummary {
  id: string;
  cardNumber?: string | undefined;
  cardHolderName?: string | undefined;
  cardSuffix?: string | undefined;
  cardType?: PaymentCardType | undefined;
  state?: string | undefined;
  isPreferred?: boolean | undefined;
}

export interface CreatePaymentInstrumentResponse {
  paymentInstrument: PaymentInstrumentSummary;
  webviewVerificationUrl?: string;
}

// The backend's Firestore converter stores fields in snake_case.
interface SavedCardDocument {
  id: string;
  card_holder_name?: string;
  card_suffix?: string;
  card_type?: PaymentCardType;
  state?: string;
  is_preferred?: boolean;
  created_at?: { toMillis?: () => number };
}

// INACTIVE is a suspended/deleted card; a failed verification can't be charged.
const HIDDEN_CARD_STATES = ["INACTIVE", "VERIFICATION_FAILED"];

const toMillis = (value: SavedCardDocument["created_at"]) =>
  value?.toMillis?.() ?? 0;

export class MePaymentInstrumentService extends ApiService {
  constructor() {
    super(process.env.REACT_APP_api_endpoint!);
  }

  async createPaymentInstrument(
    body: CreatePaymentInstrumentRequest,
  ): Promise<CreatePaymentInstrumentResponse> {
    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to add a payment card.");
    }

    const { data } = await this.post<CreatePaymentInstrumentResponse>(
      "/me/payment-instruments",
      body,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );

    if (!data.paymentInstrument?.id) {
      throw new Error("The payment service returned an invalid card response.");
    }

    return data;
  }

  // Saved cards live in Firestore at users/{uid}/paymentInstruments -- the backend writes them
  // there on every create/update and deletes the document on delete. There is no list
  // endpoint, so reading it here is what lets saved cards
  // survive a logout, a cleared cache or a different browser. Firestore rules only allow the
  // signed-in rider to read their own users/{uid} documents.
  async getSavedPaymentInstruments(): Promise<PaymentInstrumentSummary[]> {
    // Waits for the persisted Firebase session to restore before reading currentUser.
    const idToken = await getCurrentIdToken();
    const userId = auth.currentUser?.uid;
    if (!idToken || !userId) {
      throw new Error("Rider authentication is required to load saved cards.");
    }

    const snapshot = await getDocs(
      collection(db, "users", userId, "paymentInstruments"),
    );

    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }) as SavedCardDocument)
      .filter((card) => !HIDDEN_CARD_STATES.includes(card.state ?? ""))
      .sort(
        (a, b) =>
          Number(Boolean(b.is_preferred)) - Number(Boolean(a.is_preferred)) ||
          toMillis(b.created_at) - toMillis(a.created_at),
      )
      .map((card) => ({
        id: card.id,
        cardHolderName: card.card_holder_name,
        cardSuffix: card.card_suffix,
        cardType: card.card_type,
        state: card.state,
        isPreferred: card.is_preferred,
      }));
  }
}
